import { type MDXComponents } from 'mdx/types'
import { type ComponentProps, type ReactNode, useState } from 'react'
import { Link } from 'react-router'
import { AuthorityGauge } from './authority-gauge'
import { BrandBadge } from './badge'
import { Callout } from './callout'
import { NetworkGraph } from './network-graph'

/**
 * Markdown links: internal paths use React Router's Link (client nav, no full
 * reload); external links open safely; hash links stay native anchors.
 */
function SmartLink({
	href = '',
	children,
	node: _node,
	...rest
}: ComponentProps<'a'> & { node?: unknown }) {
	const isInternal = href.startsWith('/') && !href.startsWith('//')
	if (isInternal) {
		return (
			<Link to={href} {...rest}>
				{children}
			</Link>
		)
	}
	const isExternal = /^https?:\/\//.test(href)
	return (
		<a
			href={href}
			{...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
			{...rest}
		>
			{children}
		</a>
	)
}

/** Inline "try the tool" prompt, dropped into articles where it's earned. */
function ToolCTA({
	title = 'Try the free competitor backlink checker',
	body = 'Run a free scan on any competitor and see their referring domains mapped in clear terms. Add your own domain to reveal your backlink gap. No account needed.',
	label = 'Open the free checker →',
	to = '/free-backlink-checker',
}: {
	title?: string
	body?: string
	label?: string
	to?: string
}) {
	return (
		<div className="not-prose border-brand bg-brand-wash my-8 flex flex-wrap items-center justify-between gap-5 rounded-2xl border-[1.5px] px-6 py-6">
			<div className="min-w-[220px] flex-1">
				<div className="font-display text-xl font-bold">{title}</div>
				<p className="mt-1 mb-0 text-[15px] text-[#4A4952]">{body}</p>
			</div>
			<Link
				to={to}
				className="bg-brand rounded-[11px] px-5 py-3 text-[15px] font-semibold whitespace-nowrap text-white no-underline hover:opacity-90"
			>
				{label}
			</Link>
		</div>
	)
}

function PullQuote({ children }: { children: ReactNode }) {
	return (
		<blockquote className="border-brand my-8 border-l-4 pl-6 not-italic">
			<span className="font-display text-2xl leading-snug font-semibold tracking-[-0.01em] text-balance">
				{children}
			</span>
		</blockquote>
	)
}

// Only these embed origins are allowed to load in an iframe. Keep in sync with
// the frame-src CSP directive in app/entry.server.tsx.
const ALLOWED_VIDEO_ORIGINS = [
	'https://www.youtube-nocookie.com/',
	'https://www.youtube.com/embed/',
	'https://player.vimeo.com/',
]

/**
 * Click-to-load video: no third-party iframe (and no third-party requests) until
 * the reader opts in. Source origin must be on the allowlist; the frame is
 * sandboxed to the minimum needed to play.
 */
function VideoEmbed({ src, title = 'Video' }: { src: string; title?: string }) {
	const [loaded, setLoaded] = useState(false)
	const allowed = ALLOWED_VIDEO_ORIGINS.some((o) => src.startsWith(o))

	if (!allowed) {
		return (
			<div className="border-hairline text-ink my-8 rounded-2xl border bg-white p-5 text-sm text-[#8A867D]">
				Unsupported video source. Allowed: YouTube, Vimeo.
			</div>
		)
	}

	return (
		<div className="border-hairline my-8 overflow-hidden rounded-2xl border bg-black">
			<div className="relative aspect-video">
				{loaded ? (
					<iframe
						src={src}
						title={title}
						loading="lazy"
						allowFullScreen
						sandbox="allow-scripts allow-same-origin allow-presentation"
						allow="fullscreen; encrypted-media; picture-in-picture"
						className="absolute inset-0 h-full w-full"
					/>
				) : (
					<button
						type="button"
						onClick={() => setLoaded(true)}
						className="group absolute inset-0 flex flex-col items-center justify-center gap-3 text-white"
					>
						<span className="bg-brand flex size-16 items-center justify-center rounded-full transition-transform group-hover:scale-105">
							<svg width="22" height="26" viewBox="0 0 22 26" fill="white">
								<path d="M0 0l22 13L0 26z" />
							</svg>
						</span>
						<span className="text-sm font-medium">Load video ({title})</span>
					</button>
				)}
			</div>
		</div>
	)
}

/**
 * Components available inside every MDX article. Base typography (h2/p/a/code/
 * table) is handled by the <Prose> wrapper; this map adds the rich brand blocks.
 */
export const mdxComponents: MDXComponents = {
	a: SmartLink,
	Callout,
	NetworkGraph,
	AuthorityGauge,
	BrandBadge,
	ToolCTA,
	PullQuote,
	VideoEmbed,
}
